import { AppShell, Text, Title, Box } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import cn from 'clsx';
import { TermsOfUseModal } from 'src/components/TermsOfUseModal/TermsOfUseModal';
import { HEADER_LINKS } from 'src/constants';
import { LayoutHeader } from './LayoutHeader';
import { LayoutFooter } from './LayoutFooter';

const PAD_X = 48;
const TARGET_WIDTH = 1120;
const MAX_WIDTH = TARGET_WIDTH + PAD_X * 2;

const HEADER_CONTENT_HEIGHT = 40;
const HEADER_PAD_Y = 16;
const HEADER_HEIGHT = HEADER_CONTENT_HEIGHT + HEADER_PAD_Y * 2;

const HERO_HEIGHT = 300;

const Section = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <AppShell.Section
      mx="auto"
      w={MAX_WIDTH}
      className={cn('relative max-w-full px-[4%] lg:px-12', className)}
    >
      {children}
    </AppShell.Section>
  );
};

type PageHeroType = {
  heading: string;
  description?: string;
  image?: string;
};

const _HERO_SECTIONS: Record<string, PageHeroType> = {
  portfolio: {
    heading: 'Portfolio',
    image: 'https://i.imgur.com/ei1wJp9.png',
  },
  defi: {
    heading: 'aPriori DeFi Guide',
    image: 'https://i.imgur.com/CDzie44.png',
  },
};

const HERO_SECTIONS = HEADER_LINKS.reduce(
  (acc, { to }) => {
    const pageName = to.slice(1);

    if (_HERO_SECTIONS[pageName]) {
      acc[pageName] = _HERO_SECTIONS[pageName];
    }

    return acc;
  },
  {} as Record<string, PageHeroType>
);

const PageHero = ({ heading, description, image }: PageHeroType) => {
  return (
    <Box
      h={HERO_HEIGHT + HEADER_HEIGHT}
      pt={HEADER_HEIGHT}
      className="relative bg-[#0F1012]"
    >
      <Section className="h-full">
        <div className="relative h-full flex flex-col justify-center">
          <Title order={2} fz={48} fw={600} c="var(--mantine-color-main-0)">
            {heading}
          </Title>
          {description && (
            <Text
              c="var(--mantine-color-main-2)"
              fz={20}
              lh={2.4}
              className="mt-2"
            >
              {description}
            </Text>
          )}
          {image && (
            <img
              src={image}
              alt={heading}
              className="absolute right-0 bottom-0 max-h-full"
            />
          )}
        </div>
      </Section>
    </Box>
  );
};

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const pageName = location.pathname.split('/')[1];
  const pageHeroInfo = HERO_SECTIONS[pageName];

  return (
    <AppShell header={{ height: HEADER_HEIGHT }}>
      <TermsOfUseModal />
      <AppShell.Header withBorder={false} className="absolute bg-transparent">
        <Section className="h-full py-4">
          <LayoutHeader />
        </Section>
        <div className="absolute bottom-0 left-0 w-full h-[1px] bg-[radial-gradient(50%_50%_at_50%_50%,#7C4DFF_0%,transparent_100%)]" />
      </AppShell.Header>
      <AppShell.Main
        pt={!!pageHeroInfo ? 0 : undefined}
        className="transition-none"
      >
        {pageHeroInfo && <PageHero {...pageHeroInfo} />}
        <div className="py-20">
          <Section>{children}</Section>
        </div>
      </AppShell.Main>
      <AppShell.Footer className="relative border-t-white/[0.14]">
        <Section className="h-full py-6">
          <LayoutFooter />
        </Section>
      </AppShell.Footer>
    </AppShell>
  );
}
